import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Button, Form } from "react-bootstrap";
import styled from "styled-components";
import { logError, LogCategory } from "utils/sentryLogger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { colors, constants, generateRoutePath, removeAllWhitespace } from "utils";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import IconInfo from "assets/Icon-Info.svg";
import { Transcript } from "components/pages/Educator/Session/SessionSummaries/Transcript";
import { useSessionAnalysis } from "hooks/useSessionAnalysis/useSessionAnalysis";
import { EmptyState } from "components/shared/EmptyState/EmptyState";
import { TabContainer, TabContent } from "../Educator/Session/SessionSummaries/SessionSummary";
import { Summary } from "components/pages/Educator/Session/SessionSummaries/Summary";
import { request } from "hooks/useOkoApi/useOkoApi";
import { useFeatureFlagStatus } from "hooks/useFeatureFlag";
import { useSessionRow } from "./useSessionRow";

const POLLING_INTERVAL_MS = 25000;
const MAX_POLLING_ATTEMPTS = 12;
const HTTP_STATUS = {
  OK: 200,
  NOT_FOUND: 404,
  BAD_GATEWAY: 502,
  SERVER_ERROR: 500,
};
const ANALYSIS_STATUS = {
  COMPLETED: 'completed',
  FAILED: 'failed',
  PROCESSING: 'processing',
};
const TAB_KEYS = {
  SUMMARY: 'summary',
  STUDENTS: 'students',
  TRANSCRIPT: 'transcript',
  QUESTIONS: 'questions',
};
const ERROR_MESSAGES = {
  ANALYSIS_NOT_FOUND: 'Analysis could not be found. Please try generating the analysis again.',
  ANALYSIS_TIMEOUT: 'The analysis is taking a bit more time than we expected. Wait a couple of minutes and hit the "Check Session" button. If no summary and transcript is filled, please run the "Generate Analysis" once more. Thanks!',
  DATA_NOT_AVAILABLE: 'Analysis completed but data is not yet available. Please try checking the session again.',
  FETCH_FAILED: 'Analysis completed but failed to load data. Please try checking the session again.',
  SESSION_NOT_FOUND: 'Session not found. Please check the session ID.',
  CHECK_FAILED: 'Failed to check session.',
  UNKNOWN_ERROR: 'An unknown error occurred.',
  ANALYSIS_GENERATION_FAILED: 'Something went wrong with the analysis generation. Please try again later. A message will be sent to the dev team. Sorry for the inconvenience.',
};

export const SessionAnalyzer = () => {
  const { authUserData, loading } = useAuthUserData();
  const history = useHistory();

  const [sessionFormData, setSessionFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();
  const [activeTabKey, setActiveTabKey] = useState(TAB_KEYS.SUMMARY);
  const [viewMode, setViewMode] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [challengeData, setChallengeData] = useState({});
  const [challengeDataErrors, setChallengeDataErrors] = useState({});
  const [isChallengeDataLoading, setIsChallengeDataLoading] = useState({});

  const {
    transcriptData,
    summaryData,
    transcriptError,
    summaryError,
    fetchTranscript,
    fetchSummary,
    clearAll,
  } = useSessionAnalysis();
  const { ownerId, sessionRow, isLoadingRow, loadSessionRow } = useSessionRow();
  // The report's gate reads this flag; until it has answered the gate reads
  // closed, and a recent report would show the notice for a moment.
  const { isLoading: isLoadingGateFlag } = useFeatureFlagStatus("deterministicReportInputs");

  useEffect(() => {
    if (analysisError || transcriptError || summaryError) {
      setError({ common: analysisError || transcriptError || summaryError });
    }
  }, [analysisError, transcriptError, summaryError])

  const resetAnalysisState = () => {
    setIsPolling(false);
    setAnalysisStatus(null);
    setAnalysisError(null);
  };

  const fetchChallengeData = useCallback(async (sessionId, index = 0) => {
    if (!challengeData[sessionId] || (challengeData[sessionId] && challengeData[sessionId].length === 0)) {
      try {
        setIsChallengeDataLoading(prev => ({ ...prev, [index]: true }));
        const response = await request(`account/session_history_challenges/${sessionId}`, {
          method: "GET",
        });
        setChallengeData(prev => ({ ...prev, [sessionId]: response }));
        setIsChallengeDataLoading(prev => ({ ...prev, [index]: false }));
      } catch (e) {
        logError("Admin session analyzer operation failed", e, { category: LogCategory.ERROR });
        const errorMessage = e?.detail || e?.message || "An error occurred while fetching challenge data.";
        setChallengeDataErrors(prev => ({ ...prev, [sessionId]: errorMessage }));
        setIsChallengeDataLoading(prev => ({ ...prev, [index]: false }));
      }
    }
  }, [challengeData]);

  const determineActiveTab = (hasSummary, hasTranscript) => {
    if (!hasSummary && hasTranscript) {
      return TAB_KEYS.TRANSCRIPT;
    }
    return TAB_KEYS.SUMMARY;
  };

  const isNotFoundError = (errorDetail) => {
    if (!errorDetail) return false;
    const detail = errorDetail.toLowerCase();
    return detail.includes("not found") || detail.includes("does not exist");
  };

  const fetchSessionData = useCallback(async (sessionId) => {
    const [summaryResult, transcriptResult] = await Promise.all([
      fetchSummary(sessionId),
      fetchTranscript(sessionId),
      loadSessionRow(sessionId),
    ]);
    return { summaryResult, transcriptResult };
  }, [fetchSummary, fetchTranscript, loadSessionRow]);

  const handleDataFetchSuccess = useCallback((summaryResult, transcriptResult) => {
    const hasData = summaryResult && transcriptResult && !summaryError;

    if (hasData) {
      setViewMode(false);
      setActiveTabKey(determineActiveTab(summaryResult, transcriptResult));
    } else {
      setViewMode(true);
    }

    return hasData;
  }, [summaryError]);

  const handleAnalysisCompleted = useCallback(async (sessionId) => {
    setAnalysisError(null);
    setIsPolling(false);

    try {
      const { summaryResult, transcriptResult } = await fetchSessionData(sessionId);
      setViewMode(false);

      const hasData = handleDataFetchSuccess(summaryResult, transcriptResult);
      if (!hasData) {
        setError({ common: ERROR_MESSAGES.DATA_NOT_AVAILABLE });
      }
    } catch (err) {
      console.error('Error in handleAnalysisCompleted:', err);

      const isGenerationError = err?.errorType === 'json_parse_error' || err?.detail?.includes('AI generated');
      const errorMessage = isGenerationError
        ? (err?.detail || ERROR_MESSAGES.ANALYSIS_GENERATION_FAILED)
        : ERROR_MESSAGES.FETCH_FAILED;

      setAnalysisError(errorMessage);
      setError({ common: errorMessage });

      if (!isGenerationError) {
        logError("Admin session analyzer operation failed", err, { category: LogCategory.ERROR });
      }
    } finally {
      setIsLoading(false);
    }
  }, [fetchSessionData, handleDataFetchSuccess]);

  const handleAnalysisFailed = (errorMessage) => {
    const message = ERROR_MESSAGES.ANALYSIS_GENERATION_FAILED;
    setAnalysisError(message);
    setIsPolling(false);
    setIsLoading(false);
    setError({ common: message });

    if (errorMessage) {
      console.error('Analysis failed with message:', errorMessage);
      logError("Analysis generation failed", null, { category: LogCategory.ERROR, errorMessage });
    }
  };

  const checkAnalysisStatus = useCallback(async () => {
    const sessionId = sessionFormData.session_id;
    if (!sessionId) {
      return null;
    }

    try {
      const response = await request(`session_summary/${sessionId}/analyze/status`, {
        method: 'GET'
      });

      setAnalysisStatus(response.status);

      if (response.status === ANALYSIS_STATUS.COMPLETED) {
        await handleAnalysisCompleted(sessionId);
        return ANALYSIS_STATUS.COMPLETED;
      }

      if (response.status === ANALYSIS_STATUS.FAILED) {
        handleAnalysisFailed(response.error_message);
        return ANALYSIS_STATUS.FAILED;
      }

      if (response.status === ANALYSIS_STATUS.PROCESSING) {
        return HTTP_STATUS.OK;
      }

      return HTTP_STATUS.OK;
    } catch (err) {
      console.error('Error during status check:', err);
      const status = err?.status;

      setIsPolling(false);
      setIsLoading(false);

      if (status === HTTP_STATUS.NOT_FOUND || isNotFoundError(err?.detail)) {
        const errorMessage = ERROR_MESSAGES.ANALYSIS_NOT_FOUND;
        setAnalysisError(errorMessage);
        setAnalysisStatus(null);
        setError({ common: errorMessage });
        logError("Analysis status check returned 404", null, { category: LogCategory.ERROR, detail: err?.detail || 'Not found' });
        return HTTP_STATUS.NOT_FOUND;
      }

      const errorMessage = ERROR_MESSAGES.ANALYSIS_GENERATION_FAILED;
      setAnalysisError(errorMessage);
      setAnalysisStatus(null);
      setError({ common: errorMessage });
      logError("Admin session analyzer operation failed", err, { category: LogCategory.ERROR });

      return status || HTTP_STATUS.SERVER_ERROR;
    }
  }, [handleAnalysisCompleted, sessionFormData.session_id]);

  useEffect(() => {
    if (summaryData || transcriptData) {
      setAnalysisError(null);
      setError(null);
    }
  }, [summaryData, transcriptData, viewMode]);

  // Asked for when the stored summary arrives, as the teacher's page does: the
  // Group Results grid and the Solo Time name join on the first tab read them.
  // Keyed on the summary alone so that typing in the form asks for nothing.
  useEffect(() => {
    if (summaryData && sessionFormData.session_id) {
      fetchChallengeData(sessionFormData.session_id, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryData]);

  useEffect(() => {
    if (activeTabKey === TAB_KEYS.QUESTIONS && sessionFormData.session_id) {
      fetchChallengeData(sessionFormData.session_id, 0);
    }
  }, [activeTabKey, sessionFormData.session_id, fetchChallengeData]);

  useEffect(() => {
    if (!isPolling || !sessionFormData.session_id) {
      return;
    }

    let attempts = 0;
    let timeoutId = null;
    let shouldStopPolling = false;

    const handleMaxAttemptsReached = async () => {
      setIsPolling(false);
      setIsLoading(false);

      try {
        const { summaryResult, transcriptResult } = await fetchSessionData(sessionFormData.session_id);

        const hasData = handleDataFetchSuccess(summaryResult, transcriptResult);
        if (!hasData) {
          setAnalysisError(ERROR_MESSAGES.ANALYSIS_TIMEOUT);
          setAnalysisStatus(null);
          setError({ common: ERROR_MESSAGES.ANALYSIS_TIMEOUT });
        }
      } catch (err) {
        console.error('Error in handleMaxAttemptsReached:', err);

        const isGenerationError = err?.errorType === 'json_parse_error' || err?.detail?.includes('AI generated');
        const errorMessage = isGenerationError
          ? (err?.detail || ERROR_MESSAGES.ANALYSIS_GENERATION_FAILED)
          : ERROR_MESSAGES.ANALYSIS_TIMEOUT;

        setAnalysisError(errorMessage);
        setAnalysisStatus(null);
        setError({ common: errorMessage });
      }
    };

    const pollStatus = async () => {
      attempts++;

      const status = await checkAnalysisStatus();

      const maxAttemptsReached = attempts >= MAX_POLLING_ATTEMPTS;
      const shouldContinuePolling = status === HTTP_STATUS.OK &&
        !shouldStopPolling &&
        !maxAttemptsReached;

      if (shouldContinuePolling) {
        console.log(`Scheduling next poll in ${POLLING_INTERVAL_MS}ms (attempt ${attempts}/${MAX_POLLING_ATTEMPTS})`);
        timeoutId = setTimeout(pollStatus, POLLING_INTERVAL_MS);
      } else {
        console.log('Stopping polling:', {
          status,
          shouldStop: shouldStopPolling,
          maxReached: maxAttemptsReached,
          attempts
        });
        if (maxAttemptsReached) {
          await handleMaxAttemptsReached();
        }
      }
    };

    timeoutId = setTimeout(pollStatus, POLLING_INTERVAL_MS);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      shouldStopPolling = true;
    };
  }, [isPolling, sessionFormData.session_id, checkAnalysisStatus, fetchSessionData, handleDataFetchSuccess]);

  if (loading) return <></>;
  if (authUserData.isAnonymous) {
    history.push(generateRoutePath(constants.ADMIN_LOGIN_ROUTE_NAME));
    return null;
  }

  const handleCheckSession = async (e) => {
    e.preventDefault();

    const sessionId = removeAllWhitespace(sessionFormData.session_id || '');
    if (!sessionId) {
      setError({ session_id: 'Session ID is required' });
      return;
    }

    clearAll();
    setIsLoading(true);
    setError(null);
    setViewMode(false);
    resetAnalysisState();

    try {
      const handleFetchError = (err) => {
        const errorDetail = err?.detail || err?.message || '';

        if (err?.errorType === 'json_parse_error' || errorDetail?.includes('AI generated')) {
          throw err;
        }

        if (isNotFoundError(errorDetail)) {
          if (errorDetail.toLowerCase().includes("session not found")) {
            throw err;
          }
          return null;
        }
        throw err;
      };

      const summaryPromise = fetchSummary(sessionId).catch(handleFetchError);
      const transcriptPromise = fetchTranscript(sessionId).catch(handleFetchError);
      // Not awaited: the tabs wait for the row on their own, and the button frees
      // up once the summary and transcript land, as before.
      loadSessionRow(sessionId);

      const [summaryResult, transcriptResult] = await Promise.all([summaryPromise, transcriptPromise]);

      setIsLoading(false);

      setTimeout(() => {
        setViewMode(true);
        setActiveTabKey(determineActiveTab(summaryResult, transcriptResult));
      }, 50);
    } catch (e) {
      setIsLoading(false);

      const isGenerationError = e?.errorType === 'json_parse_error' || e?.detail?.includes('AI generated');

      if (isGenerationError) {
        const errorMessage = e?.detail || ERROR_MESSAGES.ANALYSIS_GENERATION_FAILED;
        setError({ common: errorMessage });
      } else {
        logError("Admin session analyzer operation failed", e, { category: LogCategory.ERROR });
        const errorMessage = e?.detail && e.detail.toLowerCase().includes("session not found")
          ? ERROR_MESSAGES.SESSION_NOT_FOUND
          : e?.detail || e?.message || ERROR_MESSAGES.CHECK_FAILED;
        setError({ common: errorMessage });
      }
    }
  };

  const handleAnalyzeSession = async (forceRegenerate = false) => {
    const sessionId = removeAllWhitespace(sessionFormData.session_id || '');
    if (!sessionId) {
      setError({ session_id: 'Session ID is required' });
      return;
    }

    clearAll();
    setIsLoading(true);
    setError(null);
    setAnalysisError(null);

    try {
      const queryParam = forceRegenerate ? '?force_regenerate=true' : '';
      await request(`session_summary/${sessionId}/analyze${queryParam}`, {
        method: 'POST'
      });

      setAnalysisStatus(ANALYSIS_STATUS.PROCESSING);
      setIsPolling(true);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      setIsPolling(false);
      logError("Admin session analyzer operation failed", err, { category: LogCategory.ERROR });

      const errors = {};
      if (err?.detail && Array.isArray(err.detail)) {
        err.detail.forEach((error) => {
          errors[error.loc.pop()] = error.msg;
        });
      } else {
        errors.common = ERROR_MESSAGES.ANALYSIS_GENERATION_FAILED;
        console.error('Analysis generation request failed:', err?.detail || err?.message || err);
      }
      setError(errors);
    }
  }

  const handleChangeSessionIdForm = (e) => {
    const { name, value } = e.target;
    const cleanedValue = removeAllWhitespace(value);
    setSessionFormData((prevState) => ({
      ...prevState,
      [name]: cleanedValue,
    }));
    setError(null);
    setViewMode(false);
    resetAnalysisState();
  };

  const handleBackToInput = () => {
    setViewMode(false);
    setError(null);
    setSessionFormData({});
    resetAnalysisState();
    clearAll();
  };

  const handleRegenerateSession = async () => {
    setViewMode(false);
    setError(null);
    await handleAnalyzeSession(true);
  };

  const isButtonDisabled = isLoading || analysisStatus === ANALYSIS_STATUS.PROCESSING || isPolling;
  const isSessionNotFound = error?.common === ERROR_MESSAGES.SESSION_NOT_FOUND;
  const showReportTabs = !isLoadingRow && !isLoadingGateFlag && !isSessionNotFound;

  const renderInfoBanner = () => (
    <InfoBannerCard>
      <StyledAlert>
        <AlertContent>
          <AlertInner>
            <InfoIcon src={IconInfo} alt="info" />
            <InfoText>
              Enter a session ID and click "Check Session". If analysis exists, you'll view it immediately.
              If not, analysis will be generated automatically. You can regenerate analysis from the viewing screen.
              Requires superuser role.
            </InfoText>
          </AlertInner>
        </AlertContent>
      </StyledAlert>
    </InfoBannerCard>
  );

  const renderPollingBanner = () => (
    <InfoBannerCard>
      <StyledAlert variant="info">
        <AlertContent>
          <AlertInner>
            <SpinnerIcon icon={faSpinner} spin />
            Generating session analysis, please wait. This may take a few minutes.
          </AlertInner>
        </AlertContent>
      </StyledAlert>
    </InfoBannerCard>
  );

  const renderFailedBanner = () => (
    <InfoBannerCard>
      <StyledAlert variant="danger">
        <AlertContent>
          <AlertInner>
            <FailedContent>
              <FailedText>
                <strong>Analysis Failed:</strong> {analysisError}
              </FailedText>
              <RegenerateButton
                disabled={isLoading || isPolling}
                type="button"
                onClick={handleAnalyzeSession}>
                Retry Analysis
              </RegenerateButton>
            </FailedContent>
          </AlertInner>
        </AlertContent>
      </StyledAlert>
    </InfoBannerCard>
  );

  const renderSuccessBanner = () => {
    const hasData = summaryData || transcriptData;
    const isNotProcessing = analysisStatus !== ANALYSIS_STATUS.PROCESSING;
    const hasErrors = summaryError || transcriptError || error;

    if (!hasData || isLoading || !isNotProcessing || viewMode || hasErrors) {
      return null;
    }

    return (
      <InfoBannerCard>
        <StyledAlert variant="success">
          <AlertContent>
            <AlertInner>
              Analysis found for session {sessionFormData.session_id}.
              {summaryData && transcriptData && " Both summary and transcript are available."}
              {summaryData && !transcriptData && " Summary is available."}
              {!summaryData && transcriptData && " Transcript is available."}
            </AlertInner>
          </AlertContent>
        </StyledAlert>
      </InfoBannerCard>
    );
  };

  const renderLoadingSpinner = () => {
    if (!isLoading || analysisStatus === ANALYSIS_STATUS.PROCESSING || viewMode) {
      return null;
    }

    return (
      <LoadingContainer>
        <FontAwesomeIcon icon={faSpinner} spin size="5x" />
      </LoadingContainer>
    );
  };

  const renderEmptyState = (message, description) => (
    <EmptyStateContainer>
      <EmptyState message={message} description={description} />
    </EmptyStateContainer>
  );

  const renderStudentsTab = () => {
    if (summaryData) {
      return (
        <Summary
          summaryData={summaryData}
          studentsOnly
          value={sessionRow}
          challengeData={challengeData}
          educatorId={ownerId}
        />
      );
    }

    return renderEmptyState(
      "Students data not available for this session.",
      ""
    );
  };

  const renderSummaryTab = ({ questionsOnly = false }) => {
    if (summaryData) {
      const sessionValue = sessionRow || {
        session_id: sessionFormData.session_id,
        activity_type: summaryData?.activity_type || constants.CONSENSUS_ENUM
      };

      return (
        <Summary
          summaryData={summaryData}
          questionsOnly={questionsOnly}
          value={sessionValue}
          index={0}
          challengeDataErrors={challengeDataErrors}
          setChallengeDataErrors={setChallengeDataErrors}
          challengeData={challengeData}
          isChallengeDataLoading={isChallengeDataLoading}
          fetchChallengeData={fetchChallengeData}
          educatorId={ownerId}
        />
      );
    }

    return renderEmptyState(
      questionsOnly ? "Questions Summary not available for this session." : "Session Summary not available for this session.",
      ""
    );
  };

  const renderTranscriptTab = () => {
    if (transcriptData) {
      return (
        <Transcript
          transcript={transcriptData}
          isLoading={false}
          error={null}
          speechTags={transcriptData?.speech_tags || []}
          betterHints={transcriptData?.better_hints || []}
        />
      );
    }

    return renderEmptyState(
      "Transcript not available for this session.",
      ""
    );
  };

  return (
    <Container>
      {!viewMode && (
        <>
          {renderInfoBanner()}

          <FormCard>
            <Form onSubmit={handleCheckSession}>
              <FormContent>
                <FormRow>
                  <FormColumn>
                    <Form.Group>
                      <FormLabel>Session Id</FormLabel>
                      <Form.Control
                        required
                        type="text"
                        placeholder="Enter session id"
                        value={sessionFormData.session_id || ""}
                        name="session_id"
                        onChange={handleChangeSessionIdForm}
                      />
                      {error?.session_id && (
                        <ErrorAlert variant="danger">{error.session_id}</ErrorAlert>
                      )}
                    </Form.Group>
                  </FormColumn>
                  <ButtonGroup>
                    <SubmitButton disabled={isButtonDisabled} type="submit">
                      Check Session
                    </SubmitButton>
                  </ButtonGroup>
                </FormRow>
                {error?.common && (
                  <ErrorAlert variant="danger">{error.common}</ErrorAlert>
                )}
              </FormContent>
            </Form>
          </FormCard>

          {isPolling && renderPollingBanner()}
          {analysisStatus === ANALYSIS_STATUS.FAILED && analysisError && renderFailedBanner()}
          {renderSuccessBanner()}
          {renderLoadingSpinner()}
        </>
      )}

      <>
        <BackButtonContainer>
          <BackButton onClick={handleBackToInput}>
            ← Back to Session Input
          </BackButton>
          <ViewRegenerateButton
            disabled={isButtonDisabled}
            onClick={handleRegenerateSession}>
            Generate Analysis
          </ViewRegenerateButton>
        </BackButtonContainer>
        {showReportTabs && (
          <ResultCard>
            <AnalyzerTabContainer
              id="controlled-tab"
              activeKey={activeTabKey}
              onSelect={(k) => setActiveTabKey(k)}
              data-active-key={activeTabKey}
            >
              <TabContent eventKey={TAB_KEYS.SUMMARY} title="Session Summary">
                {renderSummaryTab({ questionsOnly: false })}
              </TabContent>
              <TabContent eventKey={TAB_KEYS.STUDENTS} title="Students" small="true">
                {renderStudentsTab()}
              </TabContent>
              <TabContent eventKey={TAB_KEYS.QUESTIONS} title="Questions Summary" small="true">
                {renderSummaryTab({ questionsOnly: true })}
              </TabContent>
              <TabContent eventKey={TAB_KEYS.TRANSCRIPT} title="Transcript" small="true">
                {renderTranscriptTab()}
              </TabContent>
            </AnalyzerTabContainer>
          </ResultCard>
        )}
      </>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  background-color: ${colors.milkGlass200};
  flex-direction: column;
  gap: 1.5rem;
`;

const InfoBannerCard = styled.div`
  align-items: center;
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0px 1px 2px 0px rgba(14, 26, 49, 0.05);
`;

const StyledAlert = styled(Alert)`
  margin-bottom: 0;
`;

const AlertContent = styled.div`
  display: flex;
  gap: 1rem;
`;

const AlertInner = styled.div`
  align-items: center;
  display: flex;
  min-height: 36px;
  gap: 0.75rem;
  flex: 1 0 0;
  padding-right: 1.75rem;
`;

const InfoIcon = styled.img`
  width: auto;
  height: auto;
`;

const InfoText = styled.span`
  flex: 1;
`;

const SpinnerIcon = styled(FontAwesomeIcon)`
  margin-right: 0.5rem;
`;

const FailedContent = styled.div`
  width: 100%;
`;

const FailedText = styled.p`
  margin-bottom: 0.75rem;
`;

const FormCard = styled.div`
  align-items: center;
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0px 1px 2px 0px rgba(14, 26, 49, 0.05);
  padding: 2rem;
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2rem;
  justify-content: space-between;
`;

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
`;

const FormLabel = styled(Form.Label)`
  font-weight: bold;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-self: end;
  min-width: 200px;
`;

const SubmitButton = styled(Button)`
  background-color: ${colors.nightlyWoods500};
  border: none;
  border-radius: 0.375rem;
  font-size: 1rem;
  height: 40px;
  padding: 0 1rem;
  width: 100%;

  &:hover {
    background-color: ${colors.nightlyWoods700};
  }
`;

const RegenerateButton = styled(Button)`
  background-color: #dc3545;
  border: none;
  border-radius: 0.375rem;
  font-size: 1rem;
  height: 40px;
  padding: 0 1rem;
  width: 100%;

  &:hover {
    background-color: #c82333;
  }
`;

const ErrorAlert = styled(Alert)`
  padding: 0.5rem;
  margin-bottom: 0;
  margin-top: 1rem;
`;

const LoadingContainer = styled.div`
  align-self: center;
  position: fixed;
  top: 50%;
`;

const ResultCard = styled.div`
  align-items: center;
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0px 1px 2px 0px rgba(14, 26, 49, 0.05);
  padding: 2rem;
`;

const BackButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 1.25rem;
  gap: 0.75rem;
`;

const BackButton = styled(Button)`
  background-color: ${colors.nightlyWoods500};
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  white-space: nowrap;

  &:hover {
    background-color: ${colors.nightlyWoods700};
  }
`;

const ViewRegenerateButton = styled(Button)`
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  white-space: nowrap;
`;

const AnalyzerTabContainer = styled(TabContainer)`
  .nav-tabs {
    gap: 3rem;
  }

  &[data-active-key="summary"]::before {
    width: 130px;
    left: 0;
    transform: translateX(0);
  }

  &[data-active-key="students"]::before {
    width: 65px;
    left: calc(130px + 3rem);
    transform: translateX(0);
  }

  &[data-active-key="questions"]::before {
    width: 145px;
    left: calc(195px + 6rem);
    transform: translateX(0);
  }

  &[data-active-key="transcript"]::before {
    width: 74px;
    left: calc(340px + 9rem);
    transform: translateX(0);
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 2.5rem 1.25rem;
`;

